// =============================================================================
// Copyright   : 2018-2021 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe, Tobias Schwarzkopf, Andreas Hesse
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : dialogs
// =============================================================================
// ---Third-party imports
import React, {useState} from 'react'
import {Header, Segment} from 'semantic-ui-react'
import {Rnd} from 'uix/chunks'
import KeyHandler, {KEYDOWN} from 'react-key-handler'

// ---Framework imports
import {isNumber} from 'ldash'
import {isSSR} from 'uix/utils'
import {usePersistentShallow, useSelector} from 'uix/hooks'
import {Sel} from 'uix/registries'
import log from 'uix/logging'
import {XMenu, HeaderSegment, AutoPortal, SplitPane} from 'uix/components'

// ---Local imports
import {ContextHelp} from './parts.js'

// ---Exports

const INITIAL_OPTIONS = {size: {width: 640, height: 400}, position: {x: null, y: null}}
export const HelpDialog = ({
  name = 'unnamedDlg',
  icon,
  children,
  header,
  onClose,
  wikiident,
  buttons,
  closeOnEsc = false,
  minWidth = 480,
  minHeight = 300,
}) => {
  let [opts, setOpts] = usePersistentShallow(name, INITIAL_OPTIONS, 2)
  let [help, setHelp] = useState(false)
  let [firstOpened, setFirstOpened] = useState(true)
  let i18n = useSelector(Sel.i18n)

  let size = {width: opts.size.width, height: opts.size.height}
  // log.x(window.innerWidth, window.innerHeight)

  let {x, y} = opts.position
  if (!isSSR) {
    if (!isNumber(x)) {
      // centered
      x = window.innerWidth / 2 - size.width / 2
    }
    if (!isNumber(y)) {
      // 30% from top
      y = window.innerHeight / 3 - size.height / 2
    }
    if (firstOpened) {
      if (x + size.width > window.innerWidth) {
        x = Math.max(0, window.innerWidth - size.width)
      }
      if (y + size.height + 8 > window.innerHeight) {
        y = Math.max(0, window.innerHeight - size.height - 8)
      }
    }
  } else {
    x = x || 0
    y = y || 0
  }
  let position = {x, y}

  let style = {
    border: 'solid 2px #ddd',
    background: '#f0f0f0',
    zIndex: 50,
  }

  let style2 = {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    paddingLeft: '2px',
    paddingRight: '2px',
    height: '100%',
  }

  let styleHeader = {
    cursor: 'move',
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    padding: '0.1em 0.1em 0.1em 0.6em',
  }

  let menu = (
    <XMenu padded floated="right">
      {wikiident && (
        <XMenu.Item
          icon="help"
          active={help}
          onClick={() => setHelp(!help)}
          title={i18n.get('help')}
        />
      )}
      <XMenu.Item icon="close" onClick={() => onClose()} title={i18n.get('Close')} />
    </XMenu>
  )

  let handleClass = `dialog_handle_${name}`
  return (
    <AutoPortal target="rootPortal">
      <Rnd
        minWidth={minWidth}
        minHeight={minHeight}
        style={style}
        bounds="window"
        size={size}
        position={position}
        onDragStop={(e, d) => {
          setOpts({position: {x: d.x, y: d.y}})
          setFirstOpened(false)
        }}
        onResize={(e, direction, ref, delta, position) => {
          setOpts({size: {width: ref.offsetWidth, height: ref.offsetHeight}, position})
          setFirstOpened(false)
        }}
        dragHandleClassName={handleClass}
      >
        {closeOnEsc && <KeyHandler keyEventName={KEYDOWN} code={'Escape'} onKeyHandle={onClose} />}
        <SplitPane vertical>
          <div style={style2}>
            <HeaderSegment
              attached="top"
              className={handleClass}
              style={styleHeader}
              icon={icon}
              header={header}
              menu={menu}
            />
            <Segment attached style={{flex: '1 1 auto', padding: '0em'}}>
              {children}
            </Segment>
            <Header attached="bottom" style={{flex: '0 0 auto', border: 'none'}}>
              {buttons}
            </Header>
          </div>
          {help && <ContextHelp beta={false} wikiident={wikiident} />}
        </SplitPane>
      </Rnd>
    </AutoPortal>
  )
}
